import React from "react";

export interface Message {
  role: "user" | "assistant" | "system";
  content: string;
}

function inlineFormat(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={match.index}>{match[2]}</strong>);
    } else if (match[3] && match[4]) {
      parts.push(
        <a
          key={match.index}
          href={match[4]}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-link"
        >
          {match[3]}
        </a>
      );
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 1 ? parts[0] : parts;
}

function formatContent(text: string): React.ReactNode[] | null {
  if (!text) return null;

  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let bulletBuffer: string[] = [];

  function flushBullets() {
    if (bulletBuffer.length === 0) return;
    elements.push(
      <ul key={`ul-${elements.length}`} className="chat-list">
        {bulletBuffer.map((item, i) => (
          <li key={i}>{inlineFormat(item)}</li>
        ))}
      </ul>
    );
    bulletBuffer = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    const bulletMatch = line.match(/^\s*[-•*+]\s+(.+)/);
    if (bulletMatch) {
      bulletBuffer.push(bulletMatch[1]);
      continue;
    }
    flushBullets();
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const Tag = (level === 1 ? "h3" : level === 2 ? "h4" : "h5") as keyof JSX.IntrinsicElements;
      elements.push(
        <Tag key={`h-${i}`} className="chat-heading">
          {inlineFormat(text)}
        </Tag>
      );
    } else if (line.trim() === "") {
      elements.push(<br key={`br-${i}`} />);
    } else {
      elements.push(
        <p key={`p-${i}`} className="chat-para">
          {inlineFormat(line)}
        </p>
      );
    }
  }
  flushBullets();
  return elements;
}

const ChatMessage: React.FC<{ message: Message }> = ({ message }) => {
  const { role, content } = message;
  return (
    <div className={`chat-message chat-message-${role}`}>
      <div className={`chat-bubble chat-bubble-${role}`}>
        {role === "assistant" ? formatContent(content) : content}
      </div>
    </div>
  );
};

export default ChatMessage;
